import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { X, Key, Copy, Check, Trash, Code, Terminal } from "@phosphor-icons/react";
import { apiClient, API } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const V1 = `${API}/v1`;

export default function ApiKeysModal({ onClose }) {
  const { t } = useLang();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/keys");
      setKeys(data);
    } catch {
      toast.error("Failed to load keys");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await apiClient.post("/keys", { name: name.trim() || "Default key" });
      setNewKey(data);
      setName("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create key");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (key_id) => {
    try {
      await apiClient.delete(`/keys/${key_id}`);
      toast.success(t("apiRevoked"));
      load();
    } catch {
      toast.error("Revoke failed");
    }
  };

  const copyKey = (value) => {
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(value).catch(() => fallbackCopy(value));
      } else {
        fallbackCopy(value);
      }
    } catch {
      fallbackCopy(value);
    }
    setCopied(true);
    toast.success(t("apiCopied"));
    setTimeout(() => setCopied(false), 1800);
  };

  const fallbackCopy = (value) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    } catch { /* clipboard unavailable */ }
  };

  const curl = `curl -X POST ${V1}/sfx \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"prompt":"airplane jet engine overhead","duration_seconds":5}'`;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="api-keys-modal">
      <div className="w-full max-w-3xl my-8 rounded-2xl border border-white/10 bg-[color:var(--surface)] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[color:var(--surface)] rounded-t-2xl">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[color:var(--accent)]/15 grid place-items-center">
              <Key size={17} weight="fill" className="text-[color:var(--accent)]" />
            </div>
            <div>
              <h2 className="font-display font-semibold text-lg tracking-tight leading-none">{t("apiTitle")}</h2>
              <p className="text-xs text-white/40 mt-1">{t("apiSubtitle")}</p>
            </div>
          </div>
          <button data-testid="api-close-btn" onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 transition-colors">
            <X size={18} className="text-white/60" />
          </button>
        </div>

        <div className="p-6 space-y-7">
          {/* Create */}
          <div>
            <div className="flex flex-col sm:flex-row gap-2.5">
              <input
                data-testid="api-key-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={60}
                placeholder={t("apiNewKeyName")}
                className="flex-1 bg-[color:var(--surface-2)] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[color:var(--accent)]/60"
              />
              <button
                data-testid="api-create-btn"
                onClick={create}
                disabled={creating}
                className="btn-primary px-5 py-2.5 rounded-xl text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-40"
              >
                <Key size={15} weight="fill" /> {creating ? t("apiCreating") : t("apiCreate")}
              </button>
            </div>
            <p className="text-[11px] text-white/35 mt-2">{t("apiMaxKeys")}</p>
          </div>

          {/* Freshly created key (shown once) */}
          {newKey && (
            <div data-testid="api-new-key-box" className="rounded-xl border border-[color:var(--accent)]/40 bg-[color:var(--accent)]/10 p-4">
              <p className="text-xs font-mono-num uppercase tracking-widest text-[color:var(--accent)] mb-1">{t("apiYourKey")}</p>
              <div className="flex items-center gap-2">
                <code data-testid="api-new-key-value" className="flex-1 text-sm text-white break-all font-mono-num bg-black/30 rounded-lg px-3 py-2">{newKey.api_key}</code>
                <button data-testid="api-copy-btn" onClick={() => copyKey(newKey.api_key)} className="shrink-0 p-2.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors">
                  {copied ? <Check size={16} className="text-[color:var(--success)]" /> : <Copy size={16} className="text-white" />}
                </button>
              </div>
              <p className="text-xs text-[color:var(--warning)] mt-2">⚠ {t("apiCopyWarn")}</p>
            </div>
          )}

          {/* Keys list */}
          <div>
            <h3 className="text-xs font-mono-num uppercase tracking-widest text-white/50 mb-3">{t("apiKeysTitle")}</h3>
            {loading ? (
              <div className="h-16 grid place-items-center">
                <div className="w-6 h-6 border-2 border-white/20 border-t-[color:var(--accent)] rounded-full animate-spin" />
              </div>
            ) : keys.length === 0 ? (
              <p className="text-sm text-white/40 py-4">{t("apiNoKeys")}</p>
            ) : (
              <div className="space-y-2" data-testid="api-keys-list">
                {keys.map((k) => (
                  <div key={k.key_id} data-testid={`api-key-row-${k.key_id}`} className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 ${k.active ? "border-white/10 bg-[color:var(--surface-2)]" : "border-white/5 bg-transparent opacity-50"}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-white truncate">{k.name}</span>
                        {k.unlimited && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[color:var(--success)]/15 text-[color:var(--success)]">{t("apiUnlimited")}</span>}
                        <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${k.active ? "bg-white/10 text-white/60" : "bg-white/5 text-white/40"}`}>{k.active ? t("apiActive") : t("apiRevoked")}</span>
                      </div>
                      <p className="text-xs text-white/40 font-mono-num mt-1 truncate">{k.key_prefix}•••••• · {k.calls} {t("apiCalls")} · {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : t("apiNeverUsed")}</p>
                    </div>
                    {k.active && (
                      <button data-testid={`api-revoke-${k.key_id}`} onClick={() => revoke(k.key_id)} className="shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/60 hover:text-[color:var(--accent)] hover:border-[color:var(--accent)]/50 transition-colors">
                        <Trash size={13} /> {t("apiRevoke")}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Docs */}
          <div>
            <h3 className="text-xs font-mono-num uppercase tracking-widest text-white/50 mb-3 flex items-center gap-1.5"><Code size={14} /> {t("apiDocsTitle")}</h3>
            <div className="space-y-2.5 text-sm text-white/70">
              <p>{t("apiDocsAuth")}</p>
              <p className="text-white/50">{t("apiDocsCredits")}</p>
              <div className="rounded-xl border border-white/10 bg-black/40 p-4 overflow-x-auto">
                <div className="flex items-center gap-1.5 text-[11px] text-white/40 mb-2 font-mono-num uppercase tracking-widest"><Terminal size={13} /> example</div>
                <pre className="text-xs text-white/85 font-mono-num whitespace-pre leading-relaxed">{curl}</pre>
              </div>
              <ul className="text-xs text-white/55 font-mono-num space-y-1 pt-1">
                <li>POST {V1}/sfx — {`{ prompt, duration_seconds?, prompt_influence?, loop? }`}</li>
                <li>POST {V1}/voice — {`{ text, voice_id, model: "v3"|"v2" }`}</li>
                <li>POST {V1}/music — {`{ prompt, duration_seconds }`}</li>
                <li>POST {V1}/video — multipart file "video" → poll poll_url</li>
                <li>GET&nbsp;&nbsp;{V1}/voices — list available voices</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
